import { useCallback, useContext, useRef } from 'react';

import { AccommodationDataContext } from 'pages/hotels/context';

import { useAtom } from 'jotai';

import Console from 'utility/Debug/Console';
import { callApi } from 'utility/Request/callApi';

import { ApiEndpoint } from 'types/Request/endpoint';

import { MetaData_FeaturedHotelViewReport } from 'proto/Hotel/Hotel';

import { fixedPositionReportAlreadySendAtom } from '../__atoms/fixedPositionReportAlreadySend';
import { FixedPositionRequestData } from '../types';

export const useFixedPositionReport = () => {
    const [
        fixedPositionViewReportAlreadySent,
        setFixedPositionViewReportAlreadySent,
    ] = useAtom(fixedPositionReportAlreadySendAtom);
    const { accommodationList } = useContext(AccommodationDataContext);
    const lastViewReport =
        accommodationList.meta?.featuredHotelViewReport || undefined;
    const lastViewReportRef =
        useRef<MetaData_FeaturedHotelViewReport[] | undefined>(lastViewReport);

    // reset on startnewSearch
    const reset = useCallback(() => {
        setFixedPositionViewReportAlreadySent(false);
    }, [setFixedPositionViewReportAlreadySent]);

    //_onSearchFinished
    const sendFixedPositionReport = useCallback(
        async (report: FixedPositionRequestData) => {
            try {
                const response = await callApi(
                    ApiEndpoint.FixedPositionReport,
                    {
                        requestData: {
                            report: JSON.stringify(report),
                        },
                    }
                );
                setFixedPositionViewReportAlreadySent(true);

                Console.info(
                    'reporting views/clicks of fix positioned hotels',
                    response
                );

                return response.parsedBody ?? { status: 'error' };
            } catch (error) {
                throw new Error(
                    `error reporting views/clicks of fix positioned hotels: ${error}`
                );
            }
        },
        [setFixedPositionViewReportAlreadySent]
    );

    const send = useCallback(
        async (response: {
            featuredHotelViewReport: MetaData_FeaturedHotelViewReport[];
        }) => {
            if (
                fixedPositionViewReportAlreadySent ||
                !response ||
                !Array.isArray(response.featuredHotelViewReport) ||
                response.featuredHotelViewReport.length < 1
            ) {
                return;
            }

            try {
                await sendFixedPositionReport({
                    views: response.featuredHotelViewReport,
                });
                setFixedPositionViewReportAlreadySent(true);
            } catch (error) {
                throw new Error(
                    `Failed to send fixed position report:: ${error}`
                );
            }
        },
        [
            sendFixedPositionReport,
            setFixedPositionViewReportAlreadySent,
            fixedPositionViewReportAlreadySent,
        ]
    );

    const sendFixedPositionViewReport = (
        report: MetaData_FeaturedHotelViewReport[]
    ) => {
        send({
            featuredHotelViewReport: report.map((entry) => ({
                ...entry,
                featured: true,
            })),
        });
    };

    const fixedPositionClickHandling = useCallback(
        async (hotelId?: number, position?: number) => {
            if (!hotelId) {
                return;
            }

            let finalPosition = position;
            if (
                lastViewReportRef.current &&
                lastViewReportRef.current?.length > 0
            ) {
                const hotelReport = lastViewReportRef.current.find(
                    (report) => report.id === hotelId
                );
                if (hotelReport) {
                    finalPosition = hotelReport.position;
                }
            }

            const report: FixedPositionRequestData = {
                click: {
                    id: hotelId,
                    position: finalPosition,
                    featured: true,
                },
            };

            if (
                !fixedPositionViewReportAlreadySent &&
                lastViewReportRef.current &&
                lastViewReportRef.current?.length > 0
            ) {
                report.views = lastViewReportRef.current.map((entry) => ({
                    ...entry,
                    featured: true,
                }));
                setFixedPositionViewReportAlreadySent(true);
            }

            try {
                await sendFixedPositionReport(report);
            } catch (error) {
                throw new Error(
                    `Failed to handle fixed position hotel click:: ${error}`
                );
            }
        },
        [
            sendFixedPositionReport,
            setFixedPositionViewReportAlreadySent,
            fixedPositionViewReportAlreadySent,
        ]
    );

    return {
        reset,
        sendFixedPositionViewReport,
        fixedPositionClickHandling,
    };
};
